/**
 * RACE WEEK, AS A STORY.
 *
 * Everything in this file is presentation arithmetic over a finished
 * `RaceWeekResult`: which beat comes after which, whose name goes on a line,
 * which of the four stacks a card was dealt into, and how a chart chain reads
 * out loud. It computes NO rules. Every value it hands the screen was produced
 * by `runRaceWeek` — the dice, the chart codes, the verbatim printed lines, the
 * fines, the feuds, the placements. If it is not in the result, it is not here.
 *
 * The one thing this file does that the engine does not is JOIN. `TvListResult`
 * carries the chain a driver read through but not the dice that produced it;
 * the dice are in the ledger, on the event the same reading emitted. Pairing
 * the two back up is a display concern, so it lives here — and it is pinned by
 * tests, because a chain drawn against the wrong dice would be a lie told in
 * mono type.
 */

import type {
  ChartId,
  ChartReading,
  PlacementLetter,
  RaceWeekResult,
  WeekCategory,
} from '../domain/raceWeek.ts';
import { citeRow } from '../domain/raceWeek.ts';
import type { DiceRecord } from '../domain/ruleEvent.ts';
import type { DriverCard } from '../domain/types.ts';

// ---------------------------------------------------------------------------
// Chart chains
// ---------------------------------------------------------------------------

/**
 * The name a chart goes by on screen. The booklet's own headings
 * ("RACE WEEK TV LIST CHART", "TV EVENT X") are too long to sit in a chain
 * arrow, so each is shortened to the words a player would actually say. The
 * full printed name is still on the citation line beside it.
 */
export const SHORT_CHART: Record<ChartId, string> = {
  'race-week-tv-list': 'TV LIST',
  'tv-interviews': 'INTERVIEW',
  'tv-event-x': 'EVENT X',
  'tv-event-y': 'EVENT Y',
  'tv-event-z': 'EVENT Z',
};

export interface ChainStep {
  chart: ChartId;
  /** 'TV LIST', 'EVENT X', … */
  chartName: string;
  code: string;
  /** The printed line, verbatim. */
  text: string;
  /** The red + white throw that produced the code, where the ledger kept it. */
  read: DiceRecord | null;
  /** The blue die thrown with it. */
  blue: DiceRecord | null;
  /** True for the row that finally spoke — the one whose text stands. */
  last: boolean;
}

/** `TV LIST 55 → EVENT X 24`. */
export function chainLabel(chain: readonly ChartReading[]): string {
  return chain.map((step) => `${SHORT_CHART[step.chart]} ${step.code}`).join(' → ');
}

/**
 * Pair each reading in a chain with the throw that produced it.
 *
 * `resolveTvDriver` pushes, per read, an 11-66 record and then the blue die,
 * in the same loop that pushes the chain entry — so record 2i belongs to chain
 * step i and record 2i+1 is its blue. Anything after 2 × chain.length was
 * rolled later by an effect (a suspension length, the weeks before a driver
 * signs) and is returned separately rather than being drawn as a chart read.
 */
export function chainSteps(
  chain: readonly ChartReading[],
  dice: readonly DiceRecord[] = [],
): ChainStep[] {
  return chain.map((step, i) => ({
    chart: step.chart,
    chartName: SHORT_CHART[step.chart],
    code: step.code,
    text: step.text,
    read: dice[i * 2] ?? null,
    blue: dice[i * 2 + 1] ?? null,
    last: i === chain.length - 1,
  }));
}

/** The rolls an effect made after the chain had finished reading. */
export function extraDice(
  chain: readonly ChartReading[],
  dice: readonly DiceRecord[] = [],
): DiceRecord[] {
  return dice.slice(chain.length * 2);
}

/**
 * Every TV LIST reading's dice, by driver, lifted off the ledger. The event a
 * reading emits is tagged `raceweek.<chart it ended on>` and names the driver
 * first in `actorIds`.
 */
export function tvDiceByDriver(week: RaceWeekResult): Map<string, DiceRecord[]> {
  const out = new Map<string, DiceRecord[]>();
  for (const event of week.events) {
    if (!TV_READING_TAGS.has(event.momentTag)) continue;
    const id = event.actorIds[0];
    if (id && !out.has(id)) out.set(id, event.dice);
  }
  return out;
}

const TV_READING_TAGS = new Set(
  (
    ['race-week-tv-list', 'tv-interviews', 'tv-event-x', 'tv-event-y', 'tv-event-z'] as ChartId[]
  ).map((id) => `raceweek.${id}`),
);

// ---------------------------------------------------------------------------
// The TV list
// ---------------------------------------------------------------------------

export type TvBeatKind = 'roll' | 'partner' | 'carry-over';

export interface TvBeat {
  id: string;
  driverId: string;
  kind: TvBeatKind;
  /**
   * The whole chain, TV LIST first. EMPTY for a carry-over and for an
   * altercation partner: neither of them rolled anything. The partner's row is
   * still named by `chart` and `code` — it is the other man's reading that
   * landed on him.
   */
  chain: ChainStep[];
  /** Rolls an effect made after the reading — a suspension length, say. */
  extra: DiceRecord[];
  chart: ChartId | null;
  code: string | null;
  /** The verbatim line that finally spoke. */
  text: string;
  placement: PlacementLetter | null;
  blue: number;
  isInterview: boolean;
  /** For a partner: the driver whose altercation dragged him in. */
  partnerOf: string | null;
  /** RWEG/p.2 — the first driver dealt onto the list counts as GOLD this week. */
  goldThisWeek: boolean;
  citation: string;
  /**
   * Where the reading actually put him, in the engine's own words — "PLACEMENT
   * C … goes to the MIDDLE group, seat 5", "takes the POLE", "will not take the
   * green flag". A POLE reading and a wreck both print no PLACEMENT letter, so
   * the letter alone cannot say how a week ended; this can.
   */
  outcome: StoryLine | null;
}

/**
 * The TV list in the order it happened: the carry-overs who joined without a
 * roll first (RWEG/p.3), then every reading as it was taken.
 */
export function tvBeats(week: RaceWeekResult): TvBeat[] {
  const dice = tvDiceByDriver(week);
  const goldId = goldFirstId(week);
  const outcomes = outcomeByDriver(week);
  const beats: TvBeat[] = [];

  for (const id of week.carryOverTvIds) {
    beats.push({
      id: `tv-carry-${id}`,
      driverId: id,
      kind: 'carry-over',
      chain: [],
      extra: [],
      chart: null,
      code: null,
      text: 'Still on television from last week — he makes the TV LIST without a roll.',
      placement: week.placements[id] ?? null,
      blue: 0,
      isInterview: false,
      partnerOf: null,
      goldThisWeek: goldId === id,
      citation: 'RWEG/p.3 — carried over onto the TV LIST',
      outcome: outcomes.get(id) ?? null,
    });
  }

  let lastRoller: string | null = null;
  for (const result of week.tvResults) {
    if (!result.drawnAsPartner) lastRoller = result.driverId;
    const rolls = result.drawnAsPartner ? [] : (dice.get(result.driverId) ?? []);
    beats.push({
      id: `tv-${result.driverId}${result.drawnAsPartner ? '-partner' : ''}`,
      driverId: result.driverId,
      kind: result.drawnAsPartner ? 'partner' : 'roll',
      chain: result.drawnAsPartner ? [] : chainSteps(result.chain, rolls),
      extra: result.drawnAsPartner ? [] : extraDice(result.chain, rolls),
      chart: result.chart,
      code: result.code,
      text: result.text,
      placement: result.placement,
      blue: result.blue,
      isInterview: result.chart === 'tv-interviews',
      partnerOf: result.drawnAsPartner ? lastRoller : null,
      goldThisWeek: goldId === result.driverId,
      citation: citeRow(result.chart, result.code),
      outcome: outcomes.get(result.driverId) ?? null,
    });
  }
  return beats;
}

/**
 * How each driver's week ended, taken from the ledger entry that seated him —
 * or the one that took him out of the race, which is written first and so
 * wins. Never re-derived: these are the engine's own sentences.
 */
export function outcomeByDriver(week: RaceWeekResult): Map<string, StoryLine> {
  const out = new Map<string, StoryLine>();
  for (const event of week.events) {
    const tag = event.momentTag;
    if (!tag.startsWith('raceweek.placement.') && !tag.startsWith('raceweek.unavailable.')) continue;
    const id = event.actorIds[0];
    if (id && !out.has(id)) out.set(id, { line: event.description, citation: event.ruleCitation });
  }
  return out;
}

/** RWEG/p.2 — whoever the engine stamped GOLD for being first on the list. */
export function goldFirstId(week: RaceWeekResult): string | null {
  const event = week.events.find((e) => e.momentTag === 'raceweek.tv-list.gold');
  return event?.actorIds[0] ?? null;
}

// ---------------------------------------------------------------------------
// The four stacks
// ---------------------------------------------------------------------------

export interface CountRoll {
  dice: number[];
  total: number | null;
  /** The engine's own sentence for the roll. */
  line: string;
  citation: string;
}

export interface StackSummary {
  key: WeekCategory;
  /** RWEG's step number for this deal. */
  step: number;
  title: string;
  /** What the booklet does with this stack, in one line. */
  rule: string;
  count: CountRoll | null;
  driverIds: string[];
}

const STACK_TAG: Record<WeekCategory, string> = {
  tv: 'raceweek.tv-list.count',
  fast: 'raceweek.fast-week.count',
  slow: 'raceweek.slow-week.count',
  uneventful: 'raceweek.uneventful.count',
};

const STACK_META: Record<WeekCategory, { step: number; title: string; rule: string }> = {
  tv: {
    step: 2,
    title: 'TV list',
    rule: '2d6 cards, each one rolled on the RACE WEEK TV LIST CHART.',
  },
  fast: {
    step: 3,
    title: 'Fast week',
    rule: 'Gold takes the pole or the top group, silver the top, bronze the middle, no trophy the bottom.',
  },
  slow: {
    step: 4,
    title: 'Slow week',
    rule: 'Gold drops to the bottom group, silver and bronze to the bubble, no trophy fails to qualify.',
  },
  uneventful: {
    step: 5,
    title: 'Uneventful week',
    rule: 'Everyone left, placed by trophy. No die roll is necessary.',
  },
};

/** Which drivers were dealt into each of RWEG/p.2's four stacks, in deal order. */
export function bucketStacks(week: RaceWeekResult): Record<WeekCategory, string[]> {
  const out: Record<WeekCategory, string[]> = { tv: [], fast: [], slow: [], uneventful: [] };
  for (const id of week.rosterOrder) {
    const category = week.categories[id];
    if (category) out[category].push(id);
  }
  return out;
}

function countRoll(week: RaceWeekResult, tag: string): CountRoll | null {
  const event = week.events.find((e) => e.momentTag === tag);
  if (!event) return null;
  const roll = event.dice[0];
  return {
    dice: roll?.dice ?? [],
    total: roll?.total ?? null,
    line: event.description,
    citation: event.ruleCitation,
  };
}

export function stacks(week: RaceWeekResult): StackSummary[] {
  const buckets = bucketStacks(week);
  return (['tv', 'fast', 'slow', 'uneventful'] as WeekCategory[]).map((key) => ({
    key,
    ...STACK_META[key],
    count: countRoll(week, STACK_TAG[key]),
    driverIds: buckets[key],
  }));
}

// ---------------------------------------------------------------------------
// Consequences
// ---------------------------------------------------------------------------

export type ConsequenceKind =
  | 'injury'
  | 'suspension'
  | 'unable'
  | 'fine'
  | 'points'
  | 'feud'
  | 'fuming'
  | 'transfer'
  | 'chip'
  | 'chip-forfeit';

export interface ConsequenceRow {
  id: string;
  /** Everyone this row is about. A feud is two drivers and one row. */
  driverIds: string[];
  /** The consequence itself: "fined 2.5 Mb", "out 3 races". */
  headline: string;
  /** Why, in the chart's own words where it printed any. */
  detail: string;
  chart: ChartId | null;
  code: string | null;
  citation: string;
}

export interface ConsequenceGroup {
  kind: ConsequenceKind;
  title: string;
  /** What this column of the board means, in one line. */
  note: string;
  rows: ConsequenceRow[];
}

const UNAVAILABLE_WORDS: Record<string, string> = {
  injury: 'injured — out this week',
  'season-injury': 'injured — out for the season',
  'car-destroyed': 'car destroyed',
  arrested: 'arrested',
  suspended: 'suspended',
  dead: 'killed in a qualifying wreck',
  quit: 'walked out on his team',
  'carried-over': 'still unavailable from an earlier week',
};

/**
 * Every consequence the week generated, grouped, deduplicated and in the order
 * a newspaper would print them: what it cost a man his race first, money next,
 * grudges after that.
 *
 * An injury and a suspension both also appear in `unableToRace`; a driver is
 * only ever listed once, in the group that says the most about him.
 */
export function consequences(week: RaceWeekResult): ConsequenceGroup[] {
  const spokenFor = new Set<string>();

  const injuries: ConsequenceRow[] = week.injuries.map((injury) => {
    spokenFor.add(injury.driverId);
    const printed = week.unableToRace.find((u) => u.driverId === injury.driverId);
    return {
      id: `injury-${injury.driverId}`,
      driverIds: [injury.driverId],
      headline: injury.duration === 'season' ? 'Out for the season' : 'Out this week',
      detail: printed?.text ?? 'injured during race week',
      chart: injury.chart,
      code: injury.code,
      citation: injury.ruleCitation,
    };
  });

  const suspensions: ConsequenceRow[] = week.suspensions.map((suspension) => {
    spokenFor.add(suspension.driverId);
    const printed = week.unableToRace.find((u) => u.driverId === suspension.driverId);
    return {
      id: `suspension-${suspension.driverId}`,
      driverIds: [suspension.driverId],
      headline: `Suspended ${suspension.races} race${suspension.races === 1 ? '' : 's'}`,
      detail:
        (printed?.text ?? 'suspended by the sanctioning body') +
        (suspension.includesThisRace ? ' Starting with this one.' : ''),
      chart: suspension.chart,
      code: suspension.code,
      citation: suspension.ruleCitation,
    };
  });

  const unable: ConsequenceRow[] = week.unableToRace
    .filter((u) => !spokenFor.has(u.driverId))
    .map((u) => ({
      id: `unable-${u.driverId}`,
      driverIds: [u.driverId],
      headline: UNAVAILABLE_WORDS[u.reason] ?? u.reason,
      detail: u.text + (u.teamWithdrew ? ' The team withdraws from the race.' : ''),
      chart: u.chart,
      code: u.code,
      citation: u.ruleCitation,
    }));

  const fines: ConsequenceRow[] = week.fines.map((fine, i) => ({
    id: `fine-${fine.driverId}-${i}`,
    driverIds: [fine.driverId],
    headline:
      fine.amount === null
        ? `Fined — no amount printed (${fine.payer})`
        : `Fined ${fine.amount} ${fine.currency} (${fine.payer})`,
    detail: fine.offense,
    chart: fine.chart,
    code: fine.code,
    citation: fine.ruleCitation,
  }));

  const points: ConsequenceRow[] = week.pointsPenalties.map((penalty, i) => ({
    id: `points-${penalty.driverId}-${i}`,
    driverIds: [penalty.driverId],
    headline: 'Forfeits this race’s points',
    detail: 'The booklet prints “and loss of points” with no number; he loses what he earns today.',
    chart: penalty.chart,
    code: penalty.code,
    citation: penalty.ruleCitation,
  }));

  const feudCite = week.events.find((e) => e.momentTag === 'raceweek.feud')?.ruleCitation ?? null;
  const feuds: ConsequenceRow[] = week.feuds.map((feud, i) => ({
    id: `feud-${feud.a}-${feud.b}-${i}`,
    driverIds: [feud.a, feud.b],
    headline: 'On the feud list',
    detail: 'They carry it to the green flag and may take it out on each other.',
    chart: null,
    code: null,
    citation: feudCite ?? 'RWEG/p.4 — altercations go on the FEUD list',
  }));

  const fumeCite = week.events.find((e) => e.momentTag === 'raceweek.fume-list')?.ruleCitation ?? null;
  const fuming: ConsequenceRow[] = week.fuming.map((id) => ({
    id: `fume-${id}`,
    driverIds: [id],
    headline: 'Takes the green fuming',
    detail: 'His week ended at PLACEMENT D. He may vent on anyone in his own group.',
    chart: null,
    code: null,
    citation: fumeCite ?? 'RWEG/p.4 — the FUME list',
  }));

  const transfers: ConsequenceRow[] = week.teamTransfers.map((transfer, i) => ({
    id: `transfer-${transfer.driverId}-${i}`,
    driverIds: [transfer.driverId],
    headline: `Signs elsewhere in ${transfer.weeks} week${transfer.weeks === 1 ? '' : 's'}`,
    detail: 'He has no car this week; the transfer completes later.',
    chart: transfer.chart,
    code: transfer.code,
    citation: transfer.ruleCitation,
  }));

  const chipCite = 'RWEG/p.3 — “PLACEMENT A, plus Performance Chip”';
  const chips: ConsequenceRow[] = Object.entries(week.chips)
    .filter(([, n]) => n > 0)
    .map(([id, n]) => ({
      id: `chip-${id}`,
      driverIds: [id],
      headline: `Carries ${n} performance chip${n === 1 ? '' : 's'} to the green`,
      detail: 'Earned in qualifying and still in hand when the flag drops.',
      chart: null,
      code: null,
      citation: chipCite,
    }));

  const forfeits: ConsequenceRow[] = week.chipsForfeited.map((forfeit, i) => ({
    id: `forfeit-${forfeit.driverId}-${i}`,
    driverIds: [forfeit.driverId],
    headline: `Forfeits ${forfeit.chips} performance chip${forfeit.chips === 1 ? '' : 's'}`,
    detail: forfeit.reason,
    chart: null,
    code: null,
    citation: forfeit.ruleCitation,
  }));

  const groups: ConsequenceGroup[] = [
    { kind: 'unable', title: 'Will not take the green', note: 'Out of the race before it starts.', rows: unable },
    { kind: 'injury', title: 'Injured', note: 'Hurt during race week.', rows: injuries },
    { kind: 'suspension', title: 'Suspended', note: 'Parked by the sanctioning body.', rows: suspensions },
    { kind: 'fine', title: 'Fines', note: 'Millions of bucks, off the driver or the team.', rows: fines },
    { kind: 'points', title: 'Points penalties', note: 'Championship points forfeited.', rows: points },
    { kind: 'feud', title: 'Feuds', note: 'Two men who will not forget it by Sunday.', rows: feuds },
    { kind: 'fuming', title: 'Fuming', note: 'Angry, and racing anyway.', rows: fuming },
    { kind: 'transfer', title: 'Team transfers', note: 'Rides changing hands.', rows: transfers },
    { kind: 'chip', title: 'Performance chips', note: 'Earned in qualifying.', rows: chips },
    { kind: 'chip-forfeit', title: 'Chips forfeited', note: 'Earned, then lost before the flag.', rows: forfeits },
  ];

  return groups.filter((group) => group.rows.length > 0);
}

// ---------------------------------------------------------------------------
// The grid
// ---------------------------------------------------------------------------

export interface GridRow {
  position: number;
  card: DriverCard;
  onTv: boolean;
  carriedOverTv: boolean;
  chips: number;
  fuming: boolean;
  /** The other man in his feud, where he has one. */
  feudWith: string[];
  placement: PlacementLetter | null;
  category: WeekCategory | null;
}

export function gridRows(week: RaceWeekResult): GridRow[] {
  const tv = new Set(week.tvDriverIds);
  const carried = new Set(week.carryOverTvIds);
  const fuming = new Set(week.fuming);
  return week.grid.map((card, i) => ({
    position: i + 1,
    card,
    onTv: tv.has(card.driverId),
    carriedOverTv: carried.has(card.driverId),
    chips: week.chips[card.driverId] ?? 0,
    fuming: fuming.has(card.driverId),
    feudWith: week.feuds
      .filter((f) => f.a === card.driverId || f.b === card.driverId)
      .map((f) => (f.a === card.driverId ? f.b : f.a)),
    placement: week.placements[card.driverId] ?? null,
    category: week.categories[card.driverId] ?? null,
  }));
}

// ---------------------------------------------------------------------------
// Bad weather
// ---------------------------------------------------------------------------

export interface WeatherReading {
  driverId: string;
  /** The printed TV EVENT Z line that cancelled qualifying. */
  text: string;
  code: string | null;
  citation: string;
}

/** The reading that cancelled qualifying, or null on a week that got run. */
export function weatherReading(week: RaceWeekResult): WeatherReading | null {
  if (!week.badWeather) return null;
  const event = week.events.find((e) => e.modifiers.some((m) => m.name === 'bad-weather'));
  const driverId = event?.actorIds[0] ?? '';
  const reading = week.tvResults.find((r) => r.driverId === driverId);
  return {
    driverId,
    text: reading?.text ?? 'Bad weather cancels qualifying; ALL drivers, PLACEMENT C.',
    code: reading?.code ?? null,
    citation: event?.ruleCitation ?? 'RWEG/p.10 — TV EVENT Z',
  };
}

// ---------------------------------------------------------------------------
// The whole week, in the order it is told
// ---------------------------------------------------------------------------

/** A sentence the ledger wrote, with the page that sanctions it. */
export interface StoryLine {
  line: string;
  citation: string;
}

export interface WeekStory {
  seed: number;
  rosterSize: number;
  starters: number;
  /** STEP ONE, in the engine's own words. */
  opening: StoryLine | null;
  /** What the ledger said when the field was finally set. */
  gridLine: StoryLine | null;
  tvCount: CountRoll | null;
  goldFirstId: string | null;
  tv: TvBeat[];
  interviews: TvBeat[];
  stacks: StackSummary[];
  consequences: ConsequenceGroup[];
  weather: WeatherReading | null;
  grid: GridRow[];
  dnq: string[];
  bubbleUnused: string[];
  /** Reveal order. The screen shows beats whose index is below the cursor. */
  beats: string[];
}

function storyLine(week: RaceWeekResult, tag: string): StoryLine | null {
  const event = week.events.find((e) => e.momentTag === tag);
  return event ? { line: event.description, citation: event.ruleCitation } : null;
}

export function weekStory(week: RaceWeekResult, rosterSize: number): WeekStory {
  const tv = tvBeats(week);
  const stackList = stacks(week);
  const groups = consequences(week);
  const weather = weatherReading(week);

  const beats: string[] = ['open', 'tv-count'];
  for (const beat of tv) beats.push(beat.id);
  const interviews = tv.filter((beat) => beat.isInterview);
  if (interviews.length > 0) beats.push('interviews');
  if (weather) beats.push('weather');
  for (const stack of stackList) if (stack.key !== 'tv') beats.push(`stack-${stack.key}`);
  for (const group of groups) beats.push(`consequence-${group.kind}`);
  beats.push('grid');

  return {
    seed: week.seed,
    rosterSize,
    starters: week.grid.length,
    opening: storyLine(week, 'raceweek.setup'),
    gridLine: storyLine(week, 'raceweek.grid'),
    tvCount: countRoll(week, STACK_TAG.tv),
    goldFirstId: goldFirstId(week),
    tv,
    interviews,
    stacks: stackList,
    consequences: groups,
    weather,
    grid: gridRows(week),
    dnq: [...week.dnq],
    bubbleUnused: [...week.bubbleUnused],
    beats,
  };
}

/** Where a beat sits in the reveal, or -1 when the week never produced it. */
export function beatIndex(story: WeekStory, id: string): number {
  return story.beats.indexOf(id);
}

/** Is this beat revealed yet? Beats the week did not produce are never shown. */
export function isRevealed(story: WeekStory, id: string, cursor: number): boolean {
  const i = story.beats.indexOf(id);
  return i !== -1 && i < cursor;
}
